import React from 'react'

interface IconProps {
	size?: number
	strokeWidth?: number
}

const baseProps = (size: number, sw: number) => ({
	width: size,
	height: size,
	viewBox: '0 0 24 24',
	fill: 'none',
	stroke: 'currentColor',
	strokeWidth: sw,
	strokeLinecap: 'round' as const,
	strokeLinejoin: 'round' as const,
})

export const SparkleGlyph: React.FC<IconProps> = ({ size = 16, strokeWidth = 2 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
		<path d="M20 3v4" />
		<path d="M22 5h-4" />
		<path d="M4 17v2" />
		<path d="M5 18H3" />
	</svg>
)

export const CloudUpload: React.FC<IconProps> = ({ size = 24, strokeWidth = 1.7 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
		<polyline points="17 8 12 3 7 8" />
		<line x1="12" y1="3" x2="12" y2="15" />
	</svg>
)

export const ChevronLeft: React.FC<IconProps> = ({ size = 16, strokeWidth = 2 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<polyline points="15 18 9 12 15 6" />
	</svg>
)

export const Check: React.FC<IconProps> = ({ size = 12, strokeWidth = 2.5 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<polyline points="20 6 9 17 4 12" />
	</svg>
)

export const XGlyph: React.FC<IconProps> = ({ size = 14, strokeWidth = 2 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<line x1="6" y1="6" x2="18" y2="18" />
		<line x1="18" y1="6" x2="6" y2="18" />
	</svg>
)

export const PlusGlyph: React.FC<IconProps> = ({ size = 14, strokeWidth = 2 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<line x1="12" y1="5" x2="12" y2="19" />
		<line x1="5" y1="12" x2="19" y2="12" />
	</svg>
)

export const FileGlyph: React.FC<IconProps> = ({ size = 18, strokeWidth = 1.8 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
		<polyline points="14 2 14 8 20 8" />
	</svg>
)

export const ImageGlyph: React.FC<IconProps> = ({ size = 18, strokeWidth = 1.8 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<rect x="3" y="3" width="18" height="18" rx="2" />
		<circle cx="9" cy="9" r="2" />
		<path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
	</svg>
)

export const SearchGlyph: React.FC<IconProps> = ({ size = 42, strokeWidth = 1.8 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<circle cx="11" cy="11" r="8" />
		<line x1="21" y1="21" x2="16.65" y2="16.65" />
	</svg>
)

export const AlertCircle: React.FC<IconProps> = ({ size = 40, strokeWidth = 1.8 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<circle cx="12" cy="12" r="10" />
		<line x1="12" y1="8" x2="12" y2="13" />
		<line x1="12" y1="16" x2="12.01" y2="16" />
	</svg>
)

export const ClockGlyph: React.FC<IconProps> = ({ size = 40, strokeWidth = 1.8 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<circle cx="12" cy="12" r="10" />
		<polyline points="12 6 12 12 16 14" />
	</svg>
)

export const RotateCcw: React.FC<IconProps> = ({ size = 40, strokeWidth = 1.8 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<path d="M3 12a9 9 0 1 0 9-9" />
		<polyline points="3 4 3 10 9 10" />
	</svg>
)

export const MapPinGlyph: React.FC<IconProps> = ({ size = 11, strokeWidth = 1.8 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
		<circle cx="12" cy="10" r="3" />
	</svg>
)

export const PlanetGlyph: React.FC<IconProps> = ({ size = 16, strokeWidth = 1.8 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<circle cx="12" cy="12" r="9" />
		<ellipse cx="12" cy="12" rx="9" ry="3.5" />
	</svg>
)

export const TrashGlyph: React.FC<IconProps> = ({ size = 16, strokeWidth = 1.9 }) => (
	<svg {...baseProps(size, strokeWidth)} aria-hidden>
		<polyline points="3 6 5 6 21 6" />
		<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		<line x1="10" y1="11" x2="10" y2="17" />
		<line x1="14" y1="11" x2="14" y2="17" />
	</svg>
)
